import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { PublishedPageKind } from '../published/publishedPageKind';
import { SmartLinkPageFacts } from './useSmartLinkPageFacts';
import { formatReleaseDay } from '../presave/presaveDates';
import { daysUntilRelease } from '../../music24/promote/releases/releaseOrder';
import styles from './SMLPageMeta.module.scss';

interface SMLPageMetaProps {
  facts: SmartLinkPageFacts;
  createdAt: number;
}

/**
 * The line under the title: what the page is, how it is doing, and when it
 * matters.
 *
 * "Smart Link · created Aug 17" told the artist what they already knew. The
 * kind, the traffic and the release day are the three things they came to
 * check. Each one is drawn only when the data behind it has arrived — the
 * created date is the only fact the page carries on its own, so it holds the
 * line while the rest is still loading.
 */
const SMLPageMetaComponent: React.FC<SMLPageMetaProps> = ({ facts, createdAt }) => {
  const intl = useIntl();

  const kindLabel = useMemo(() => {
    switch (facts.kind) {
      case PublishedPageKind.presave:
        return intl.formatMessage({ id: 'app.sml.details.kind.presave', defaultMessage: 'Pre-save page' });
      case PublishedPageKind.bioLink:
        return intl.formatMessage({ id: 'app.sml.details.kind.bio', defaultMessage: 'Bio link' });
      case PublishedPageKind.release:
      default:
        return intl.formatMessage({ id: 'app.sml.details.kind.release', defaultMessage: 'Release page' });
    }
  }, [facts.kind, intl]);

  const countdown = useMemo(() => {
    if (facts.releaseDate === null) {
      return null;
    }
    // The rule of the release page and Promote: calendar days to the release day (QA lit-9).
    const days = Math.max(0, daysUntilRelease(facts.releaseDate, new Date()) ?? 0);
    return intl.formatMessage(
      { id: 'app.sml.details.meta.out', defaultMessage: 'out {date}, in {days, plural, one {# day} other {# days}}' },
      { date: formatReleaseDay(intl, facts.releaseDate), days }
    );
  }, [facts.releaseDate, intl]);

  return (
    <p className={styles.meta}>
      <span className={styles.pill}>{kindLabel}</span>
      {facts.viewCount !== null && (
        <span className={styles.item}>
          {intl.formatMessage(
            {
              id: 'app.sml.details.meta.views',
              defaultMessage: '{count, plural, one {# view} other {# views}} in {days} days',
            },
            { count: facts.viewCount, days: facts.viewDays }
          )}
        </span>
      )}
      {countdown !== null && <span className={styles.item}>{countdown}</span>}
      <span className={styles.item}>
        {intl.formatMessage(
          { id: 'app.sml.details.meta.created', defaultMessage: 'created {date}' },
          { date: intl.formatDate(createdAt, { day: 'numeric', month: 'short' }) }
        )}
      </span>
    </p>
  );
};

export const SMLPageMeta = TypedMemo(SMLPageMetaComponent);
